import React, { useEffect, useState, useMemo } from 'react';
import { Form, Typography, Button, Card, Divider, Checkbox, Table, message } from 'antd';
import { SaveOutlined, UnorderedListOutlined } from '@ant-design/icons';
import CommonForm from '../../../global/form';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import SOURCES from '../../../../utils/constants';
import { cn } from '../../../../utils/cn';
import Spinner from '../../../global/spinner';

const { Title, Text } = Typography;

const EnhancedMailChimp = ({ destinations, activeDestination, btnClassName }) => {
    const [form] = Form.useForm();
    const [campaignList, setCampaignList] = useState([]);
    const [selectedCampaigns, setSelectedCampaigns] = useState([]);
    const apiKey = Form.useWatch('apiKey', form);
    const serverZone = Form.useWatch('serverZone', form);

    const DESTINATION_MAILCHIMP = SOURCES.DESTINATION_MAILCHIMP || "mailchimp";

    // Query to get campaigns
    const { data: campaignsData, isFetching: isCampaignsLoading, refetch: refetchCampaigns } = useGenericQuery({
        queryKey: [QUERY_KEYS.mailChimpCampaigns.all, apiKey, serverZone],
        endpoint: "integration/mailchimp-campaigns",
        queryParams: {
            apiKey: apiKey,
            serverZone: serverZone
        },
        enabled: false,
        onSuccess: (data) => {
            setCampaignList(data || []);
        }
    });

    const { mutate: saveMailChimp, isPending: isSaving } = useGenericMutation({
        endpoint: "integration",
        method: "POST",
        onSuccess: () => message.success("MailChimp configuration saved successfully")
    });

    useEffect(() => {
        if (campaignsData) {
            setCampaignList(campaignsData || []);
        }
    }, [campaignsData]);

    useEffect(() => {
        if (destinations) {
            const config = destinations.find(d => d.integrationCompany === DESTINATION_MAILCHIMP);
            if (config) {
                form.setFieldsValue({
                    apiKey: config.secretKey,
                    serverZone: config.publicKey,
                    sendLeadsLimit: config.leadSentConfig?.sentLimit || 0
                });
                if (config.locations) setSelectedCampaigns(config.locations);

                if (config.secretKey && config.publicKey) {
                    refetchCampaigns();
                }
            }
        }
    }, [destinations, form]);

    const onFinish = (values) => {
        saveMailChimp({
            secretKey: values.apiKey,
            publicKey: values.serverZone,
            locations: selectedCampaigns,
            sendLeadsLimit: values.sendLeadsLimit,
            integrationCompany: DESTINATION_MAILCHIMP,
        });
    };

    const handleFetchCampaigns = () => {
        const apiKey = form.getFieldValue("apiKey");
        const serverZone = form.getFieldValue("serverZone");
        if (apiKey && serverZone) {
            refetchCampaigns();
        } else {
            message.warning("Please enter API Key and Server Zone first");
        }
    };

    const handleCampaignToggle = (id, checked) => {
        if (checked) {
            setSelectedCampaigns(prev => [...prev, id.toString()]);
        } else {
            setSelectedCampaigns(prev => prev.filter(cId => cId !== id.toString()));
        }
    };

    const columns = [
        {
            title: "Select",
            dataIndex: "id",
            key: "id",
            width: 80,
            render: (id) => (
                <Checkbox
                    checked={selectedCampaigns.includes(id.toString())}
                    onChange={(e) => handleCampaignToggle(id, e.target.checked)}
                />
            )
        },
        {
            title: "Campaign Name",
            dataIndex: "name",
            key: "name",
            render: (text) => <Text strong>{text}</Text>
        }
    ];

    const formData = useMemo(() => [
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "API Key",
            name: "apiKey",
            placeholder: "Enter MailChimp API Key",
            required: true,
            span: 12,
            onChange: (e) => {
                const parts = e.target.value.split("-");
                if (parts.length > 1) {
                    form.setFieldsValue({ serverZone: parts[1] });
                }
            }
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Server Zone",
            name: "serverZone",
            placeholder: "e.g. us20",
            required: true,
            span: 12,
            extra: <Text type="secondary" className="text-[10px]">Example: https://<Text type="danger" strong>us20</Text>.admin.mailchimp.com</Text>
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            span: 24,
            component: (
                <div className="space-y-4">
                    <Button
                        type="dashed"
                        icon={isCampaignsLoading ? null : <UnorderedListOutlined />}
                        onClick={handleFetchCampaigns}
                        className="h-10 w-full border-dashed hover:border-primary hover:!text-primary"
                    >
                        {isCampaignsLoading && <Spinner className={"!border-primary"} />}
                        Get Campaigns
                    </Button>
                    {campaignList.length > 0 && (
                        <div className="animate-in slide-in-from-top-2">
                            <Table
                                dataSource={campaignList}
                                columns={columns}
                                pagination={false}
                                size="small"
                                rowKey="id"
                                className="border border-gray-100 rounded-lg overflow-hidden"
                            />
                        </div>
                    )}
                </div>
            )
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Initial Lead Send Limit",
            name: "sendLeadsLimit",
            placeholder: "Leads limit",
            type: "number",
            span: 24,
        }
    ], [campaignList, selectedCampaigns, isCampaignsLoading]);

    return (
        <div className="animate-in fade-in duration-500">
            <div className="flex items-center gap-4 mb-8">
                <div className="size-16 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center p-3">
                    <img src={activeDestination.icon} alt={activeDestination.title} className="w-full h-full object-contain" />
                </div>
                <div>
                    <Title level={3} className="!mb-1">{activeDestination.title} Configuration</Title>
                    <Text type="secondary">Connect your MailChimp account and sync leads to campaigns.</Text>
                </div>
            </div>

            <Card className="rounded-2xl border-none shadow-sm">
                <CommonForm
                    form={form}
                    data={formData}
                    onFinish={onFinish}
                    layoutType="vertical"
                />
                <Divider className="mb-4 border-gray-100" />
                <div className="flex justify-start">
                    <Button
                        size="large"
                        icon={isSaving ? null : <SaveOutlined />}
                        onClick={() => form.submit()}
                        className={cn(btnClassName)}
                    >
                        {isSaving && <Spinner className={"!border-white"} />}
                        Save Configuration
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default EnhancedMailChimp;
